/**
 * The logic backend's wire types.
 *
 * Field names are the `@JsonProperty` values from `domain-logic`, so this file
 * is the contract: renaming anything here silently breaks a screen rather than
 * failing the build. Enums are string unions rather than TS enums for the same
 * reason — the values on the wire are the enum constant names.
 */

export interface Entity {
  id: number;
}

// ── shared ────────────────────────────────────────────────────────────────

export type S3ObjectStatus = "ACTIVE" | "ARCHIVED";

export interface S3ObjectDTO extends Entity {
  bucket_name?: string;
  content_type?: string;
  file_name?: string;
  file_path?: string;
  url?: string;
  status?: S3ObjectStatus;
  created_at?: string;
}

export interface Tuple2<A, B> {
  first: A;
  second: B;
}

export type Currency = "USD" | "CAD";
export type Geozone =
  | "NORTH_AMERICA"
  | "SOUTH_AMERICA"
  | "EUROPE"
  | "AFRICA"
  | "ASIA"
  | "OCEANIA";

// ── artwork ───────────────────────────────────────────────────────────────

export type ArtworkStatus = "ACTIVE" | "INCOMPLETE" | "TERMINATED";
export const ARTWORK_STATUSES: ArtworkStatus[] = ["ACTIVE", "INCOMPLETE", "TERMINATED"];

export type ArtworkEdition = "OPEN" | "CLOSED";
export const ARTWORK_EDITIONS: ArtworkEdition[] = ["OPEN", "CLOSED"];

export type ArtworkSyncStatus = "ACTIVE" | "SUSPENDED";

export type ArtworkTagType = "COLOR" | "THEME" | "KEYWORD";
export const ARTWORK_TAG_TYPES: ArtworkTagType[] = ["COLOR", "THEME", "KEYWORD"];

export type ArtworkImageType =
  | "HR_ORIGINAL"
  | "HR_STANDARDIZED"
  | "HR_CORRECTED"
  | "THUMBNAIL"
  | "SMALL"
  | "MEDIUM"
  | "LARGE";
export const ARTWORK_IMAGE_TYPES: ArtworkImageType[] = [
  "HR_ORIGINAL",
  "HR_STANDARDIZED",
  "HR_CORRECTED",
  "THUMBNAIL",
  "SMALL",
  "MEDIUM",
  "LARGE",
];

export type AspectRatioStrategy = "RESIZE" | "CROP" | "PAD";
export const ASPECT_RATIO_STRATEGIES: AspectRatioStrategy[] = ["RESIZE", "CROP", "PAD"];

export type Orientation = "CUSTOM" | "SQUARE" | "HORIZONTAL" | "VERTICAL";
export const ORIENTATIONS: Orientation[] = ["CUSTOM", "SQUARE", "HORIZONTAL", "VERTICAL"];

export type Ratio = "CUSTOM" | "SQUARE" | "HV133" | "HV150" | "HV175" | "HV200" | "HV233";
export const RATIOS: Ratio[] = ["CUSTOM", "SQUARE", "HV133", "HV150", "HV175", "HV200", "HV233"];

/** Numeric aspect values, mirroring the `Ratio` enum's constructor argument. */
export const RATIO_VALUES: Record<Ratio, number> = {
  CUSTOM: 0,
  SQUARE: 1,
  HV133: 1.33,
  HV150: 1.5,
  HV175: 1.75,
  HV200: 2,
  HV233: 2.33,
};

export interface ArtworkMetadataDTO extends Entity {
  artwork_id?: number;
  code: string;
  publisher_code: string;
  edition: ArtworkEdition;
  aspect_ratio_strategy: AspectRatioStrategy;
  orientation: Orientation;
  ratio: Ratio;
  max_print_width: number;
  max_print_height: number;
  dpi?: number | null;
  title: string;
  artist?: string | null;
}

export interface ArtworkImageDTO extends Entity {
  artwork_id: number;
  type: ArtworkImageType;
  width: number;
  height: number;
  image?: S3ObjectDTO | null;
}

export interface ArtworkTagDTO extends Entity {
  artwork_id: number;
  type: ArtworkTagType;
  value: string;
}

export interface ArtworkStoreSyncDTO extends Entity {
  artwork_id: number;
  store: StoreDTO;
  status: ArtworkSyncStatus;
  synced_at?: string | null;
  ref_id?: string | null;
}

export interface ArtworkCoreDTO extends Entity {
  uuid?: string;
  publisher: PublisherDTO;
  catalog: CatalogDTO;
  status: ArtworkStatus;
  created_at?: string;
  updated_at?: string;
  metadata: ArtworkMetadataDTO;
}

/** The grid read model: no image list, one thumbnail. */
export interface LazyArtworkDTO extends ArtworkCoreDTO {
  collections: CollectionDTO[];
  store_syncs: ArtworkStoreSyncDTO[];
  thumbnail?: ArtworkImageDTO | null;
}

/** The detail read model: every image, every tag. */
export interface ArtworkDTO extends ArtworkCoreDTO {
  images: ArtworkImageDTO[];
  tags: ArtworkTagDTO[];
  collections: CollectionDTO[];
  store_syncs: ArtworkStoreSyncDTO[];
}

export interface ArtworkSearchDTO extends Entity {
  code: string;
  title: string;
  artist?: string | null;
  thumbnail?: ArtworkImageDTO | null;
}

// ── publisher / catalog / collection ──────────────────────────────────────

export type PublisherType = "PERSON" | "GROUP" | "ORGANIZATION";
export const PUBLISHER_TYPES: PublisherType[] = ["PERSON", "GROUP", "ORGANIZATION"];

export type PublisherStatus = "ACTIVE" | "TERMINATED";
export const PUBLISHER_STATUSES: PublisherStatus[] = ["ACTIVE", "TERMINATED"];

export interface PublisherDTO extends Entity {
  name: string;
  code: string;
  type: PublisherType;
  status: PublisherStatus;
  /** Percent, 0–100. The grid shows it divided by 100. */
  royalty: number;
}

export type PublisherMetadataField =
  | "CODE"
  | "PUBLISHER_CODE"
  | "TITLE"
  | "ARTIST"
  | "TAG_THEME"
  | "TAG_COLOR"
  | "TAG_KEYWORD";
export const PUBLISHER_METADATA_FIELDS: PublisherMetadataField[] = [
  "CODE",
  "PUBLISHER_CODE",
  "TITLE",
  "ARTIST",
  "TAG_THEME",
  "TAG_COLOR",
  "TAG_KEYWORD",
];

export type PublisherMetadataSplitMode = "SINGLE" | "COMMA_SEPARATED";
export const PUBLISHER_SPLIT_MODES: PublisherMetadataSplitMode[] = ["SINGLE", "COMMA_SEPARATED"];

export interface PublisherMetadataTemplateFieldMappingDTO extends Entity {
  template_id: number;
  column_header: string;
  field: PublisherMetadataField;
  split_mode: PublisherMetadataSplitMode;
  sort_order: number;
}

export interface PublisherMetadataTemplateDTO extends Entity {
  publisher_id: number;
  name: string;
  header_row: number;
  created_at?: string;
  updated_at?: string;
  field_mappings: PublisherMetadataTemplateFieldMappingDTO[];
}

export interface CatalogDTO extends Entity {
  name: string;
}

export interface CollectionDTO extends Entity {
  name: string;
}

export type DisplayCollectionImageOwner = "ARTWORK" | "PRODUCT";
export const DISPLAY_COLLECTION_OWNERS: DisplayCollectionImageOwner[] = ["ARTWORK", "PRODUCT"];

export interface DisplayCollectionImageLineDTO extends Entity {
  display_collection_id: number;
  image_id: number;
  owner: DisplayCollectionImageOwner;
  sort_order: number;
  artwork_id?: number | null;
  artwork_image?: ArtworkImageDTO | null;
  product_image?: ProductImageDTO | null;
}

export interface DisplayCollectionDTO extends Entity {
  uuid?: string;
  name: string;
  /** BCrypt hash. Set it by hashing client-side, exactly as Vaadin did. */
  password?: string;
  created_at?: string;
  images: DisplayCollectionImageLineDTO[];
}

// ── store / client ────────────────────────────────────────────────────────

export interface StoreDTO extends Entity {
  client_id?: number | null;
  code: string;
  name: string;
  currency: Currency;
  is_default: boolean;
  url: string;
  gid: string;
}

export type ClientType = "CUSTOMER" | "COMPANY";
export const CLIENT_TYPES: ClientType[] = ["CUSTOMER", "COMPANY"];

export interface MailingAddressDTO extends Entity {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  company?: string | null;
  country_code?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  province_code?: string | null;
  zip?: string | null;
}

export interface ClientMailingAddressDTO extends MailingAddressDTO {
  client_id: number;
  is_default: boolean;
  gid?: string | null;
  company_location_gid?: string | null;
}

export interface ClientNoteDTO extends Entity {
  client_id: number;
  account_id: number;
  note: string;
  created_at?: string;
  updated_at?: string;
}

export interface ClientDTO extends Entity {
  type: ClientType;
  code: string;
  name: string;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  currency: Currency;
  customer_gid?: string | null;
  company_gid?: string | null;
  company_contact_gid?: string | null;
  addresses: ClientMailingAddressDTO[];
  notes: ClientNoteDTO[];
  feed_types: FeedType[];
}

// ── region ────────────────────────────────────────────────────────────────

export interface RegionDTO extends Entity {
  country_id: number;
  name: string;
  iso_3166_code: string;
}

export interface CountryDTO extends Entity {
  name: string;
  iso_3166_code: string;
  geozone: Geozone;
  regions: RegionDTO[];
}

// ── product ───────────────────────────────────────────────────────────────

export type ProductFamily = "FINISHED" | "GIANT" | "PAPER" | "MISC" | "NO_PRINT";
export const PRODUCT_FAMILIES: ProductFamily[] = ["FINISHED", "GIANT", "PAPER", "MISC", "NO_PRINT"];

export type ProductStatus = "ACTIVE" | "TERMINATED";
export const PRODUCT_STATUSES: ProductStatus[] = ["ACTIVE", "TERMINATED"];

export type ProductDistributionStatus = "OPEN" | "RESTRICTED";
export const PRODUCT_DISTRIBUTION_STATUSES: ProductDistributionStatus[] = ["OPEN", "RESTRICTED"];

export type ProductImageType = "DECOR";

export interface ProductCategoryDTO extends Entity {
  family: ProductFamily;
  code: string;
  name: string;
  material?: string | null;
  color?: string | null;
  label: string;
  framed: boolean;
  assembled: boolean;
}

export interface ProductMetadataDTO extends Entity {
  product_id: number;
  short_description?: string;
  long_description?: string;
  keywords: string[];
  features: string[];
  package_width?: number | null;
  package_height?: number | null;
  package_depth?: number | null;
  package_weight?: number | null;
}

export interface ProductDTO extends Entity {
  category: ProductCategoryDTO;
  code: string;
  status: ProductStatus;
  distribution_status: ProductDistributionStatus;
  orientation: Orientation;
  ratio: Ratio;
  width: number;
  height: number;
  print_width: number;
  print_height: number;
  depth: number;
  weight: number;
  metadata?: ProductMetadataDTO | null;
}

export interface ProductImageDTO extends Entity {
  artwork_id: number;
  product_id: number;
  decor_id?: number | null;
  type: ProductImageType;
  image?: S3ObjectDTO | null;
}

export interface ArtworkProductMetadataDTO extends Entity {
  artwork_id: number;
  product_id: number;
  upc?: string | null;
}

export interface ProductStorePriceLineDTO extends Entity {
  product_id: number;
  store_id: number;
  price: number;
}

export interface ProductClientPriceLineDTO extends Entity {
  product_id: number;
  client_id: number;
  price: number;
}

/** The artwork × product join used by the artwork detail screen. */
export interface ProductEntityDTO {
  artwork_id: number;
  sku: string;
  product: ProductDTO;
  metadata?: ArtworkProductMetadataDTO | null;
  images: ProductImageDTO[];
  store_prices: ProductStorePriceLineDTO[];
  client_prices: ProductClientPriceLineDTO[];
}

export interface GlobalDpiDTO extends Entity {
  dpi: number;
}

// ── decor ─────────────────────────────────────────────────────────────────

export type DecorStatus = "ACTIVE" | "DELETED";
export const DECOR_STATUSES: DecorStatus[] = ["ACTIVE", "DELETED"];

export type DecorType = "DECOR" | "PRODUCT";
export const DECOR_TYPES: DecorType[] = ["DECOR", "PRODUCT"];

export interface DecorFrameFront {
  resolution?: Tuple2<number, number> | null;
  [key: string]: unknown;
}

export interface DecorFrame {
  offset?: Tuple2<number, number> | null;
  front?: DecorFrameFront | null;
  [key: string]: unknown;
}

export interface DecorDTO extends Entity {
  product_id: number;
  code: string;
  status: DecorStatus;
  type: DecorType;
  sort_order: number;
  psd_file?: S3ObjectDTO | null;
  thumbnail?: S3ObjectDTO | null;
  background_layer?: S3ObjectDTO | null;
  frame?: DecorFrame | null;
  tags: string[];
}

// ── joliscreen ────────────────────────────────────────────────────────────

export type JoliscreenImageStatus = "ACTIVE" | "INACTIVE";
export const JOLISCREEN_STATUSES: JoliscreenImageStatus[] = ["ACTIVE", "INACTIVE"];

export interface JoliscreenImageDTO extends Entity {
  artwork_id: number;
  image?: S3ObjectDTO | null;
  status: JoliscreenImageStatus;
  store_url?: string | null;
  width: number;
  height: number;
  title?: string | null;
  artist?: string | null;
  tags: string[];
  created_at?: string;
}

// ── print settings ────────────────────────────────────────────────────────

export type BorderType = "MIRROR" | "WRAP" | "SOLID" | "NONE";
export const BORDER_TYPES: BorderType[] = ["MIRROR", "WRAP", "SOLID", "NONE"];

export type OutputType = "PRESERVE_FORMAT" | "JPEG";
export const OUTPUT_TYPES: OutputType[] = ["PRESERVE_FORMAT", "JPEG"];

export interface PrintSettingsDTO extends Entity {
  border_type: BorderType;
  h_border_in_inches: number;
  v_border_in_inches: number;
  h_white_space_in_inches: number;
  v_white_space_in_inches: number;
  rotation: number;
  output_type: OutputType;
  maximum_file_size_mb?: number | null;
}

export interface PrintSettingsLineDTO extends Entity {
  print_settings_id: number;
  product_id: number;
  production_center_id: number;
}

// ── production ────────────────────────────────────────────────────────────

export type ProductionCenterType = "INTERNAL" | "EXTERNAL";
export const PRODUCTION_CENTER_TYPES: ProductionCenterType[] = ["INTERNAL", "EXTERNAL"];

export type ProductionJobStatus =
  | "PENDING"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELED"
  | "FAILED"
  | "ARCHIVED";
export const PRODUCTION_JOB_STATUSES: ProductionJobStatus[] = [
  "PENDING",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELED",
  "FAILED",
  "ARCHIVED",
];

export type ProductionJobLineStatus =
  | "PENDING"
  | "READY_TO_PRINT"
  | "PRINTED"
  | "SHIPPED"
  | "SUBMITTED"
  | "CANCELED"
  | "FAILED";
export const PRODUCTION_JOB_LINE_STATUSES: ProductionJobLineStatus[] = [
  "PENDING",
  "READY_TO_PRINT",
  "PRINTED",
  "SHIPPED",
  "SUBMITTED",
  "CANCELED",
  "FAILED",
];

export type LabelSheetType =
  | "FRAMING_JOB"
  | "FRAMING_JOB_W_IMAGES"
  | "OL75WX"
  | "OL875WX"
  | "OL125WX_MOMA";
export const LABEL_SHEET_TYPES: LabelSheetType[] = [
  "FRAMING_JOB",
  "FRAMING_JOB_W_IMAGES",
  "OL75WX",
  "OL875WX",
  "OL125WX_MOMA",
];

export interface ProductionCenterDTO extends Entity {
  name: string;
  type: ProductionCenterType;
  priority: number;
  countries: CountryDTO[];
  products: ProductDTO[];
}

export interface ProductionJobLineDTO extends Entity {
  production_job_id: number;
  artwork_id?: number | null;
  product_id?: number | null;
  hr_image?: S3ObjectDTO | null;
  production_image?: S3ObjectDTO | null;
  line_item_id?: string | null;
  quantity: number;
  aspect_ratio_strategy: AspectRatioStrategy;
  status: ProductionJobLineStatus;
  error_message?: string | null;
  snap_artwork_code?: string | null;
  snap_artwork_title?: string | null;
  snap_artwork_artist?: string | null;
  snap_product_code?: string | null;
  snap_product?: string | null;
  external_order_id?: string | null;
  submitted_at?: string | null;
  created_at?: string;
  updated_at?: string;
  printed_at?: string | null;
  shipped_at?: string | null;
}

export interface ProductionJobCoreDTO extends Entity {
  order_id: number;
  production_center_id: number;
  status: ProductionJobStatus;
  created_at?: string;
  updated_at?: string;
  lines: ProductionJobLineDTO[];
}

export interface ProductionJobDTO extends ProductionJobCoreDTO {
  order: OrderDTO;
  production_center: ProductionCenterDTO;
}

export interface SkuMatchDTO extends Entity {
  sku: string;
  artwork_id: number;
  product_id: number;
}

export type LegacyProductionJobStatus =
  | "ON_HOLD"
  | "IN_PROGRESS"
  | "FAILED"
  | "CANCELLED"
  | "COMPLETED";
export const LEGACY_JOB_STATUSES: LegacyProductionJobStatus[] = [
  "ON_HOLD",
  "IN_PROGRESS",
  "FAILED",
  "CANCELLED",
  "COMPLETED",
];

export interface LegacyProductionJobDTO extends Entity {
  artwork_id?: number | null;
  product_id?: number | null;
  ufra_job_id?: number | null;
  ufra_job_id_id: number;
  width: number;
  height: number;
  borders: string;
  printer: string;
  size_adjustment_method: string;
  production_image_id?: number | null;
  status: LegacyProductionJobStatus;
  notes: string[];
  archived: boolean;
  start_date?: string | null;
  end_date?: string | null;
  created_at?: string;
  updated_at?: string;
  production_image?: S3ObjectDTO | null;
}

/** Dispatch request accepted by `POST /production/job/dispatch`. */
export interface OrderDispatchRequest {
  order_header_id: number;
  line_item_id: string;
  line_item_sku: string;
  line_item_title: string;
  line_item_vendor: string;
  artwork_id?: number | null;
  product_id?: number | null;
  aspect_ratio_strategy: AspectRatioStrategy;
  production_center_id: number;
  quantity: number;
}

// ── orders (Shopify-backed) ───────────────────────────────────────────────

export type OrderHeaderDispatchStatus =
  | "NOT_DISPATCHABLE"
  | "NOT_DISPATCHED"
  | "PARTIAL_DISPATCH"
  | "DISPATCHED";
export const DISPATCH_STATUSES: OrderHeaderDispatchStatus[] = [
  "NOT_DISPATCHABLE",
  "NOT_DISPATCHED",
  "PARTIAL_DISPATCH",
  "DISPATCHED",
];

/**
 * Shopify's own vocabulary, carried through untouched — these strings come
 * straight from the GraphQL API via `displayFulfillmentStatus` /
 * `displayFinancialStatus`.
 */
export type OrderFulfillmentStatus =
  | "UNFULFILLED"
  | "PARTIALLY_FULFILLED"
  | "FULFILLED"
  | "RESTOCKED"
  | "PENDING_FULFILLMENT"
  | "OPEN"
  | "IN_PROGRESS"
  | "ON_HOLD"
  | "SCHEDULED"
  | "REQUEST_DECLINED";
export const FULFILLMENT_STATUSES: OrderFulfillmentStatus[] = [
  "UNFULFILLED",
  "PARTIALLY_FULFILLED",
  "FULFILLED",
  "RESTOCKED",
  "PENDING_FULFILLMENT",
  "OPEN",
  "IN_PROGRESS",
  "ON_HOLD",
  "SCHEDULED",
  "REQUEST_DECLINED",
];

export type OrderFinancialStatus =
  | "PENDING"
  | "AUTHORIZED"
  | "PARTIALLY_PAID"
  | "PARTIALLY_REFUNDED"
  | "PAID"
  | "REFUNDED"
  | "VOIDED"
  | "EXPIRED";
export const FINANCIAL_STATUSES: OrderFinancialStatus[] = [
  "PENDING",
  "AUTHORIZED",
  "PARTIALLY_PAID",
  "PARTIALLY_REFUNDED",
  "PAID",
  "REFUNDED",
  "VOIDED",
  "EXPIRED",
];

export interface Money {
  amount: string;
  currencyCode: string;
}

export interface MoneyBag {
  shopMoney: Money;
  presentmentMoney?: Money;
}

export interface ShopifyMailingAddress {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  company?: string | null;
  countryCodeV2?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  phone?: string | null;
  provinceCode?: string | null;
  zip?: string | null;
}

export interface ShopifyCustomer {
  id?: string;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface ShopifyProductVariantRef {
  id?: string;
  sku?: string | null;
  title?: string | null;
  barcode?: string | null;
  price?: string | null;
  compareAtPrice?: string | null;
  image?: { url?: string | null } | null;
  selectedOptions?: Array<{ name: string; value: string }>;
}

export interface ShopifyLineItem {
  id: string;
  name?: string | null;
  title?: string | null;
  quantity?: number;
  unfulfilledQuantity?: number;
  vendor?: string | null;
  variant?: ShopifyProductVariantRef | null;
  originalTotalSet?: MoneyBag;
  image?: { url?: string | null } | null;
}

export interface ShopifyMetafield {
  key: string;
  value: string;
}

export interface ShopifyOrder {
  id: string;
  name?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  displayFulfillmentStatus?: string | null;
  displayFinancialStatus?: string | null;
  fulfillable?: boolean;
  customer?: ShopifyCustomer | null;
  shippingAddress?: ShopifyMailingAddress | null;
  billingAddress?: ShopifyMailingAddress | null;
  lineItems?: { nodes: ShopifyLineItem[] };
  metafields?: { nodes: ShopifyMetafield[] };
  totalPriceSet?: MoneyBag;
  note?: string | null;
  tags?: string[];
}

export interface ShopifyDraftOrder extends Omit<ShopifyOrder, "displayFulfillmentStatus"> {
  status?: string | null;
}

export interface OrderHeaderDTO extends Entity {
  store_id: number;
  client_id?: number | null;
  order_id: number;
  order_no: string;
  client_order_no?: string | null;
  snap_client_name?: string | null;
  snap_client_code?: string | null;
  snap_client?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface OrderDTO extends OrderHeaderDTO {
  order: ShopifyOrder;
}

export interface DraftOrderDTO extends OrderHeaderDTO {
  draft_order: ShopifyDraftOrder;
}

export interface OrderNoteDTO extends Entity {
  order_type: string;
  order_id: number;
  account_id: number;
  note: string;
  created_at?: string;
  updated_at?: string;
}

export interface SkuSalesDTO {
  sku: string;
  units: number;
  sales: number;
}

/** `POST /shopify/order/draft/create` body. */
export interface NewDraftOrderRequest {
  store: { store: StoreDTO };
  client: {
    client: ClientDTO;
    billing_address: ClientMailingAddressDTO | null;
    currency: Currency;
    order_no?: string | null;
    order_date?: string | null;
  };
  customer?: { name?: string; email?: string; phone?: string } | null;
  line_items: Array<{
    artwork_id: number;
    product: ProductDTO;
    quantity: number;
    width: number;
    height: number;
    price: number;
  }>;
  mailing: {
    requires_shipping: boolean;
    billing_address: ClientMailingAddressDTO | null;
    shipping_address: ClientMailingAddressDTO | null;
  };
  misc: { note?: string | null; tags?: string | null };
}

// ── shopify collections / products ────────────────────────────────────────

export type ShopifyCatalog = "GIANT_ART" | "CURIOOS";
export const SHOPIFY_CATALOGS: ShopifyCatalog[] = ["GIANT_ART", "CURIOOS"];
export const SHOPIFY_CATALOG_LABELS: Record<ShopifyCatalog, string> = {
  GIANT_ART: "Artwork",
  CURIOOS: "Curioos",
};

export interface ShopifyCollection {
  id: string;
  title?: string | null;
  handle?: string | null;
  updatedAt?: string | null;
  productsCount?: { count: number } | null;
  ruleSet?: unknown;
}

export interface ShopifyCollectionDTO extends Entity {
  collection_id: string;
  store: StoreDTO;
  rules?: unknown;
}

export interface ShopifyPublication {
  id: string;
  name?: string | null;
}

export interface ShopifyProduct {
  id: string;
  title?: string | null;
  status?: string | null;
  handle?: string | null;
  onlineStoreUrl?: string | null;
  updatedAt?: string | null;
  priceRange?: {
    minVariantPrice: Money;
    maxVariantPrice: Money;
  } | null;
  variants?: { nodes: ShopifyProductVariantRef[] };
}

// ── kpi ───────────────────────────────────────────────────────────────────

export interface KpiSales {
  sales_units_today: number;
  sales_usd_today: number;
  sales_units_7_days: number;
  sales_usd_7_days: number;
  sales_units_30_days: number;
  sales_usd_30_days: number;
  sales_units_90_days: number;
  sales_usd_90_days: number;
  sales_units_month: number;
  sales_usd_month: number;
  sales_units_last_month: number;
  sales_usd_last_month: number;
  sales_units_quarter: number;
  sales_usd_quarter: number;
  sales_units_last_quarter: number;
  sales_usd_last_quarter: number;
  sales_units_ytd: number;
  sales_usd_ytd: number;
  sales_units_last_year: number;
  sales_usd_last_year: number;
}

export interface KpiClientDTO extends Entity, KpiSales {
  client?: ClientDTO | null;
  computed_at?: string;
}

export interface KpiStoreDTO extends Entity, KpiSales {
  store?: StoreDTO | null;
  computed_at?: string;
}

export type KpiTotalSalesDTO = KpiSales & Partial<Entity>;

// ── feeds ─────────────────────────────────────────────────────────────────

export type FeedType =
  | "AMAZON_COM_VENDOR"
  | "ARTCOM"
  | "BISON_OFFICE"
  | "COSTCO_NEXT"
  | "COSTCO_NEXT_INVENTORY"
  | "CYMAX"
  | "FRANKLIN_MINT"
  | "HOUZZ"
  | "LOWES_CORE"
  | "LOWES_INITIATE"
  | "LOWES_ITEM_PROPOSAL"
  | "MACYS"
  | "OVERSTOCK_PRINT"
  | "OVERSTOCK_CANVAS"
  | "PERIGOLD"
  | "PIER1"
  | "RAYMOUR"
  | "RUUMUR"
  | "SIMONS"
  | "THE_BAY"
  | "THE_BRICK"
  | "THE_BRICK_PART_2"
  | "TOUCH_OF_MODERN"
  | "WALMART"
  | "WALMART_INVENTORY"
  | "WAYFAIR";

/** Labels straight from the `FeedType` enum, so the pickers read the same. */
export const FEED_TYPE_LABELS: Record<FeedType, string> = {
  AMAZON_COM_VENDOR: "Amazon.com Vendor",
  ARTCOM: "Art.com",
  BISON_OFFICE: "Bison Office",
  COSTCO_NEXT: "Costco Next",
  COSTCO_NEXT_INVENTORY: "Costco Next Inventory",
  CYMAX: "Cymax",
  FRANKLIN_MINT: "Franklin Mint",
  HOUZZ: "Houzz",
  LOWES_CORE: "Lowes Core",
  LOWES_INITIATE: "Lowes Initiate",
  LOWES_ITEM_PROPOSAL: "Lowes Item Proposal",
  MACYS: "Macy's",
  OVERSTOCK_PRINT: "Overstock Print",
  OVERSTOCK_CANVAS: "Overstock Canvas",
  PERIGOLD: "Perigold",
  PIER1: "Pier 1",
  RAYMOUR: "Raymour",
  RUUMUR: "Ruumur",
  SIMONS: "Simons",
  THE_BAY: "The Bay",
  THE_BRICK: "The Brick",
  THE_BRICK_PART_2: "The Brick Part 2",
  TOUCH_OF_MODERN: "Touch of Modern",
  WALMART: "Walmart",
  WALMART_INVENTORY: "Walmart Inventory",
  WAYFAIR: "Wayfair",
};

export const FEED_TYPES = Object.keys(FEED_TYPE_LABELS) as FeedType[];

export type MimeImageType = "JPG" | "PNG";
